import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { ApplicationException } from 'common/exceptions/application/ApplicationException';
import { ProcessedMessages } from 'common/models/ProcessedMessage';
import { addToLoaderDetails } from 'common/utils/loaderUtil';
import CONFIG from 'config';
import { Complaint } from '../models/Complaint';
import { Interaction } from '../models/Interaction';
import { SupportedOfficeItem } from '../utils/SupportedOfficeItem';
import * as Api from '../utils/api';
import * as Auth from '../utils/authUtils';
import { getAllStakeholders } from '../utils/contactUtils';
import { toIsoWithTimeZone } from '../utils/dateUtils';
import * as GraphApi from '../utils/graphApi';
import { getDirectionForGraphItem } from '../utils/graphItemUtils';
import {
  getDateFromOutlookItem,
  getSubjectFromGraphMessage,
} from '../utils/outlookItemUtils';
import * as ProductAnalytics from '../utils/product-analytics';
import * as UserSettings from '../utils/user-settings';
import { ContactCustomFields } from './../models/ContactCustomFields';
import * as App from './app';
import * as AppContacts from './contacts';
import { errorHandler } from './errorHandler';
import * as ItemProperties from './item-properties';
import * as AppSave from './save';

const TAG = 'ATS3: ';

export async function isAutosaved(item: SupportedOfficeItem) {
  if (!CONFIG.isGraphApiEnabled) return false;

  if (item.itemType === 'appointment') return false;

  console.log(TAG, 'Checking if item was autosaved');
  const messageDate = await getDateFromOutlookItem(item);
  // message date will be compared to last sync date, which doesn't include milliseconds,
  // because REST API doesn't include them in date-time fields
  // to make the comparison "fair", messageDate should be stripped of milliseconds
  messageDate.setMilliseconds(0);
  const contacts = await getAutosaveContacts();

  // Get all the email addresses from the email, but:
  // Exclude the user's email address
  // Exclude CC-ed email addresses [includeCc = false]
  const stakeholders = await getAllStakeholders(item, undefined, false);

  // find all autosave contacts for this item
  const itemContacts: MicrosoftGraph.Contact[] = [];
  for (const stakeholder of stakeholders) {
    const contact = contacts.find(
      (c) =>
        c.emailAddresses?.[0]?.address?.toLowerCase() ===
        stakeholder.email?.toLowerCase()
    );
    if (contact) {
      itemContacts.push(contact);
    }
  }

  // if no autosave contacts for this item, then item is not autosaved
  if (itemContacts.length === 0) {
    console.log(TAG, 'Contacts on SSTKAutoSave', contacts);
    console.log(
      TAG,
      'The contact is checking -> Message is not supposed to be autosaved'
    );
    return false;
  }

  // check last sync dates for all contacts
  const autosavedForAllContacts = itemContacts.every((c) => {
    console.log(TAG, `Checking if item was autosaved for ${c.givenName}`);
    let customFields: ContactCustomFields;
    try {
      customFields = AppContacts.getCustomFields(c);
    } catch (error) {
      console.error(
        TAG,
        'Failed to parse custom fields for contact while checking isAutosaved:',
        c.emailAddresses?.[0]?.address,
        'Raw personalNotes value read from Outlook:',
        c.personalNotes,
        'Error:',
        error
      );
      throw error;
    }
    console.log(
      TAG,
      `Last sync date for contact ${customFields.lastSyncDate}`,
      Number(customFields.lastSyncDate)
    );
    console.log(TAG, `Message date is  ${messageDate}`, Number(messageDate));

    return (
      customFields.lastSyncDate >= messageDate &&
      customFields.startAutosaveDate <= messageDate
    );
  });

  console.log(TAG, 'Autosaved for all contacts ', autosavedForAllContacts);
  return autosavedForAllContacts;
}

export async function autosaveNewMessages() {
  console.log(TAG, 'Getting contacts to auto save new messages');
  const contacts = await getAutosaveContacts();

  await prepareMessages(contacts);

  // Filter contacts to include only those with messages
  const contactsWithMessages = contacts.filter((contact) => {
    const address = contact.emailAddresses[0].address;
    const hasMsg = hasMessages(address);

    if (!hasMsg) {
      console.log(TAG, `Contact with no messages to sync: ${address}`);
    }

    return hasMsg;
  });

  let messageSaved = false;
  let totalMessages = 0;
  const errorContacts = [];
  const processedMessages: ProcessedMessages = { count: 0 };

  // Calculate the total number of messages
  for (const contact of contactsWithMessages) {
    let customFields: ContactCustomFields;
    try {
      customFields = AppContacts.getCustomFields(contact);
    } catch (error) {
      console.error(
        TAG,
        'Failed to parse custom fields for contact while calculating total messages in autosaveNewMessages:',
        contact.emailAddresses?.[0]?.address,
        'Raw personalNotes value read from Outlook:',
        contact.personalNotes,
        'Error:',
        error
      );
      throw error;
    }
    const messages = getMessages(
      contact.emailAddresses[0].address,
      new Date(customFields.lastSyncDate)
    );
    totalMessages += messages.length;
  }

  if (totalMessages > 0) {
    const loaderDetails = 'Autosaving Started';
    addToLoaderDetails({
      text: loaderDetails,
      secondaryText:
        'Please avoid any interruptions, such as changing or opening emails, while autosaving is active.',
    });
    console.log(TAG, loaderDetails);
  }

  for (const contact of contactsWithMessages) {
    try {
      const result = await autosaveMessagesForContact(
        contact,
        processedMessages,
        totalMessages
      );

      // Update messageSaved if the result is true
      if (result) {
        messageSaved = true;
        console.log(
          TAG,
          'Completed processing new messages for contact with new messages:',
          contact.emailAddresses[0].address,
          'result:',
          result
        );
      }
    } catch (error) {
      // Collect error email addresses
      const contactEmail =
        contact.emailAddresses[0]?.address || '(No Email Address)';
      errorContacts.push(contactEmail);

      console.error(
        TAG,
        `Error processing contact with new messages: ${contactEmail}`,
        error
      );
    }
  }

  if (totalMessages > 0) {
    const loaderDetails = 'Autosaving Completed';
    addToLoaderDetails(loaderDetails);
    console.log(TAG, loaderDetails);
  }

  if (messageSaved) {
    await ProductAnalytics.trackEvent({ eventName: 'used_email_plugin' });
  }

  if (errorContacts.length > 0) {
    // Show error popup with collected error contacts
    const errorMessage = `Error while autosaving messages for the following contacts:<br><br>${errorContacts.join(
      '<br>'
    )}<br><br>Please contact support.`;
    errorHandler(null, errorMessage);
  }

  // Reset the loader to "Loading"
  addToLoaderDetails(null);
}

export async function getAutosaveContacts() {
  const group = await GraphApi.getOrCreateContactGroup(CONFIG.contactGroupName);
  const contacts = await GraphApi.getContacts(group.id);
  console.log(TAG, 'Group Info ', group.id + '-' + group.displayName);
  console.log(TAG, 'Found contacts ', contacts);
  console.log(TAG, 'Total contacts ', contacts.length);
  return contacts;
}

async function autosaveMessagesForContact(
  contact: MicrosoftGraph.Contact,
  processedMessages: ProcessedMessages,
  totalMessages: number
) {
  if (!contact.emailAddresses[0] || !contact.emailAddresses[0].address) {
    throw new ApplicationException(
      'One of the contacts in autosave contact group is corrupt: ' +
        JSON.stringify(contact)
    );
  }

  console.log(
    TAG,
    'Processing autosave contact for:',
    contact.emailAddresses[0].address
  );

  let customFields: ContactCustomFields;
  try {
    customFields = AppContacts.getCustomFields(contact);
  } catch (error) {
    console.error(
      TAG,
      'Failed to parse custom fields for contact while processing autosaveMessagesForContact:',
      contact.emailAddresses?.[0]?.address,
      'Raw personalNotes value read from Outlook:',
      contact.personalNotes,
      'Error:',
      error
    );
    throw error;
  }

  if (!isCorrectClientCode(customFields)) {
    return false;
  }

  // previous version didn't set customFields.lastSyncDate
  // in this case, use LastModifiedDateTime instead
  // can be removed later
  const lastSyncDate = !isNaN(Number(customFields.lastSyncDate))
    ? customFields.lastSyncDate
    : new Date(contact.lastModifiedDateTime);

  console.log(
    TAG,
    `Searching for new messages from ${contact.emailAddresses[0].address} since ${lastSyncDate}. Custom fields:`,
    customFields
  );

  const messages = getMessages(contact.emailAddresses[0].address, lastSyncDate);

  console.log(
    TAG,
    `Found ${messages.length} messages for ${contact.emailAddresses[0].address}.`
  );

  if (messages.length === 0) {
    return false;
  }

  UserSettings.setProject(customFields.projectId);
  await Auth.refreshToken();

  const stakeholderId = await Api.findStakeholder(customFields.stakeholder);

  if (!stakeholderId) {
    console.log(
      TAG,
      `${customFields.stakeholder.Email} was deleted in ${CONFIG.appName}`
    );
    console.log(
      TAG,
      'Deleting contact ',
      contact.displayName + '-' + contact.emailAddresses?.[0]?.address
    );
    await GraphApi.deleteContact(contact.id);
    throw new ApplicationException(
      `Stakeholder ${customFields.stakeholder.Email} was deleted in ${CONFIG.appName}. ` +
        'Add-in will stop auto-saving messages from this stakeholder'
    );
  }

  let messageSaved = false;

  console.log(TAG, 'messages: ', messages);

  for (const message of messages) {
    console.log(TAG, 'Process Message: ', message.subject);
    const shouldSave = await shouldSaveMessage(message);
    if (shouldSave) {
      await autosaveMessage(message, customFields, stakeholderId);
      await ItemProperties.markItemAsSavedThroughGraph(message);

      messageSaved = true;
      console.log(TAG, 'Saved as Interaction: ', message.subject);
    }

    // even if message was skipped because it's already saved,
    // we'll need to update last sync date so message would be marked as Autosaved in UI
    await AppContacts.setLastSyncDate(
      contact,
      new Date(message.receivedDateTime)
    );

    // Update the progress percentage
    processedMessages.count++;

    const loaderDetails = `Autosaving In Progress (${processedMessages.count} / ${totalMessages})`;
    addToLoaderDetails({
      text: loaderDetails,
      secondaryText:
        'Please avoid any interruptions, such as changing or opening emails, while autosaving is active.',
    });
    console.log(TAG, loaderDetails);

    // Check if token still valid, if not then refresh the token.
    // This will prevent the subsequent endpoints from failing.
    if (!Auth.isTokenValid()) {
      await Auth.refreshToken();
    }
  }

  return messageSaved;
}

let allNewMessages: MicrosoftGraph.Message[];
/** Retrieves and saved messages for all contacts in one go
 *  Because requesting them for individual contacts is costly
 */
async function prepareMessages(contacts: MicrosoftGraph.Contact[]) {
  const addressesToDates = new Map<string, Date>();
  for (const contact of contacts) {
    let fields: ContactCustomFields;
    try {
      fields = AppContacts.getCustomFields(contact);
    } catch (error) {
      console.error(
        TAG,
        'Failed to parse custom fields for contact while preparing messages in prepareMessages:',
        contact.emailAddresses?.[0]?.address,
        'Raw personalNotes value read from Outlook:',
        contact.personalNotes,
        'Error:',
        error
      );
      throw error;
    }
    const lastSyncDate = fields.lastSyncDate;

    if (
      contact.emailAddresses &&
      contact.emailAddresses[0] &&
      contact.emailAddresses[0].address
    ) {
      const address = contact.emailAddresses[0].address;
      addressesToDates.set(address, lastSyncDate);
    }
  }

  console.log(TAG, 'Getting messages no older than:');
  addressesToDates.forEach((date, address) => {
    console.log(TAG, `${address}: ${date}`);
  });
  const allMessages = await GraphApi.getMessages(addressesToDates);

  const contactMessagesAscending = allMessages.sort((a, b) => {
    const aDate = new Date(a.receivedDateTime);
    const bDate = new Date(b.receivedDateTime);

    if (aDate > bDate) return 1;
    else if (bDate > aDate) return -1;
    return 0;
  });

  allNewMessages = contactMessagesAscending;
}

/**
 * Filters previously retrieved messages
 */
function getMessages(email: string, lastSyncDate: Date) {
  email = email.toLowerCase();

  const contactMessages = allNewMessages.filter((m) => {
    // REST API might round up time (e.g. 9:00:00 900ms -> 9:00:01)
    // we're adding a second to lastSyncDate to catch messages with round up seconds
    const clone = new Date(lastSyncDate);
    const adjustedLastSyncDate = new Date(
      clone.setSeconds(clone.getSeconds() + 1)
    );

    if (new Date(m.receivedDateTime) <= adjustedLastSyncDate) {
      return false;
    }

    if (
      m.from.emailAddress &&
      m.from.emailAddress.address?.toLowerCase() === email
    ) {
      return true;
    }

    if (!m.toRecipients) return false;

    const recipient = m.toRecipients.find(
      (r) => r.emailAddress.address?.toLowerCase() === email
    );

    if (recipient) return true;
    else return false;
  });

  return contactMessages;
}

function hasMessages(email: string): boolean {
  email = email.toLowerCase();

  const hasMessage = allNewMessages.some((m) => {
    if (
      m.from.emailAddress &&
      m.from.emailAddress.address?.toLowerCase() === email
    ) {
      return true;
    }

    if (!m.toRecipients) return false;

    const recipient = m.toRecipients.find(
      (r) => r.emailAddress.address?.toLowerCase() === email
    );

    return recipient ? true : false;
  });

  return hasMessage;
}

/** Returns false if current domain/client doesn't match the one assigned for this contact */
function isCorrectClientCode(customFields: ContactCustomFields) {
  // if it's not saved, we'll just try to save the message with current domain/client and assign it to this contact later

  const client = App.clients.find((c) => c.Name == customFields.clientId);

  if (UserSettings.getDomain()) {
    if (customFields.clientId && customFields.domainUrl) {
      if (!client || customFields.domainUrl !== UserSettings.getDomain()) {
        console.log(
          TAG,
          'Signed in with different domain or client code, skipping this contact'
        );
        return false;
      } else {
        console.log(TAG, 'Signed in correct domain/client, continuing');
      }
    } else {
      console.log(TAG, 'Domain/client not set for this contact');
    }
  }

  if (client && client.Name != UserSettings.getClientCode()) {
    UserSettings.setClientCode(client.Name);
  }

  return true;
}

async function shouldSaveMessage(message: MicrosoftGraph.Message) {
  const isMarkedAsSaved = await ItemProperties.isItemSavedThroughGraph(message);
  if (isMarkedAsSaved) {
    console.log(TAG, 'Message already saved, skipping', message.subject);
    return false;
  }

  const isExcludedPromise = Api.ensureEmailSubjectIsNotExcluded({
    Subject: message.subject,
  });
  const isSavedPromise = isEmailADuplicate(message);
  const isExcluded = await isExcludedPromise;
  const isSavedOnServer = await isSavedPromise;

  if (isExcluded) {
    console.log(
      TAG,
      'Email subject contains words that are in the exclusion list'
    );
    return false;
  }
  if (isSavedOnServer) {
    console.log(TAG, 'Email was already saved on the server');
    return false;
  }

  return true;
}

async function isEmailADuplicate(message: MicrosoftGraph.Message) {
  if (CONFIG.allowToSaveMessageAgain) return false;

  const response = await Api.findDuplicateEmail({
    Subject: getSubjectFromGraphMessage(message),
    EmailAddresses: [
      message.from.emailAddress.address,
      ...message.toRecipients.map((to) => to.emailAddress.address),
    ],
    Message: message.body.content,
    DateTime: toIsoWithTimeZone(new Date(message.receivedDateTime)),
  });
  console.log(TAG, `is Email Duplicate: `, response.IsMatchFound);
  return response.IsMatchFound;
}

async function autosaveMessage(
  message: MicrosoftGraph.Message,
  customFields: ContactCustomFields,
  stakeholderId: string
) {
  console.log(TAG, 'message is now saving... StakeholderID: ', stakeholderId);
  console.log(
    TAG,
    'message is now saving... from Email address: ',
    message?.from?.emailAddress?.address
  );

  if (customFields.emailType == 'Communication' || !customFields.emailType) {
    await saveInteraction(message, customFields, stakeholderId);
  } else if (customFields.emailType == 'Complaint') {
    await saveComplaint(message, customFields, stakeholderId);
  } else {
    throw new ApplicationException(
      'Unexpected email type for auto saving ' + customFields.emailType
    );
  }
}

async function saveInteraction(
  message: MicrosoftGraph.Message,
  customFields: ContactCustomFields,
  stakeholderId: string
) {
  console.log(TAG, 'Saving interaction ', message.subject, customFields);

  const attachmentsIds = await AppSave.uploadAttachmentsForItemId(
    message.id,
    'message',
    undefined
  );

  const {
    messageBody: interactionMessageBody,
    largeInlineAttachmentIds: interactionLargeInlineIds,
  } = await AppSave.updateInlineAttachments(
    message.body.content,
    message.id,
    'message',
    undefined
  );

  message.body.content = interactionMessageBody;

  const interaction = await getInteractionForAutosave(message, customFields);
  AppSave.captureMessageReferenceIdsForGraphMessage(interaction, message);
  const interactionId = await Api.saveInteraction(interaction);
  await Api.linkInteractionToStakeholders(interactionId, [stakeholderId]);
  const allAutosaveInteractionAttachmentIds = [
    ...attachmentsIds,
    ...interactionLargeInlineIds,
  ];
  if (allAutosaveInteractionAttachmentIds.length > 0) {
    await Api.linkInteractionToAttachments(
      interactionId,
      allAutosaveInteractionAttachmentIds
    );
  }

  await ProductAnalytics.trackEvent({
    eventName: 'new_interaction_saved_from_outlook',
  });

  await ProductAnalytics.trackEvent({
    eventName: 'outlook_message_autosaved_interaction_old',
  });
}

async function saveComplaint(
  message: MicrosoftGraph.Message,
  customFields: ContactCustomFields,
  stakeholderId: string
) {
  console.log(TAG, 'Saving complaint ', message.subject);

  const attachmentsIds = await AppSave.uploadAttachmentsForItemId(
    message.id,
    'message',
    undefined
  );

  const {
    messageBody: complaintMessageBody,
    largeInlineAttachmentIds: complaintLargeInlineIds,
  } = await AppSave.updateInlineAttachments(
    message.body.content,
    message.id,
    'message',
    undefined
  );

  message.body.content = complaintMessageBody;

  const complaint = await getComplaintForAutosave(message, customFields);
  const complaintId = await Api.saveComplaint(complaint);
  await Api.linkComplaintToStakeholders(complaintId, [stakeholderId]);
  const allAutosaveComplaintAttachmentIds = [
    ...attachmentsIds,
    ...complaintLargeInlineIds,
  ];
  if (allAutosaveComplaintAttachmentIds.length > 0) {
    await Api.linkComplaintToAttachments(
      complaintId,
      allAutosaveComplaintAttachmentIds
    );
  }

  await ProductAnalytics.trackEvent({
    eventName: 'new_complaint_saved_from_outlook',
  });
}

async function getInteractionForAutosave(
  item: MicrosoftGraph.Message,
  customFields: ContactCustomFields
) {
  const subject = getSubjectFromGraphMessage(item);
  const direction = await getDirectionForGraphItem(item);
  const dateTime = new Date(item.receivedDateTime);
  const body = item.body.content;
  const template = customFields.communicationTemplate;

  const communicationDirection = await Api.getDirectionFromServer(direction);

  const interaction: Interaction = {
    Subject: subject,
    Location: null,
    CommunicationDate: toIsoWithTimeZone(dateTime),
    Method: {
      Text: 'Email',
    },
    Direction: communicationDirection,
    Message: body,
    Confidential: template.Confidential,
    Duration: null, // there is no autosave for meetings
    InteractionType: template.InteractionType,
    SentimentPhrases: template.SentimentPhrases,
    AdditionalSharedProjectIDs: template.AdditionalSharedProjectIDs,
  };

  return interaction;
}

async function getComplaintForAutosave(
  item: MicrosoftGraph.Message,
  customFields: ContactCustomFields
) {
  const subject = getSubjectFromGraphMessage(item);
  const direction = await getDirectionForGraphItem(item);
  const dateTime = new Date(item.receivedDateTime);
  const body = item.body.content;
  const template = customFields.communicationTemplate;

  const communicationDirection = await Api.getDirectionFromServer(direction);

  const complaint: Complaint = {
    Subject: subject,
    AssignedTo: template.AssignedTo,
    DateOpened: toIsoWithTimeZone(dateTime),
    Method: {
      Text: 'Email',
    },
    Direction: communicationDirection,
    Message: body,
    Priority: template.Priority,
    Confidential: template.Confidential,
    Status: template.Status,
    InteractionType: template.InteractionType,
    SentimentPhrases: template.SentimentPhrases,
  };

  return complaint;
}
